import { voiceAgentStore } from "../../store/voiceAgentStore";

/**
 * Enhanced Audio Playback System
 *
 * Features:
 * 1. Instant Start: No artificial delay. Plays immediately when data arrives.
 * 2. Gapless Playback: Uses precise timing to schedule chunks back-to-back.
 * 3. Buffering State: Pauses and signals UI when data runs out, resumes automatically.
 * 4. No Speed Alteration: Always plays at 1.0x speed.
 */

export class AudioPlayback {
  private audioContext: AudioContext | null = null;
  private audioQueue: Float32Array[] = [];
  private isPlaying: boolean = false;
  private nextStartTime: number = 0;
  private sampleRate: number = 24000;
  private channelCount: number = 1;

  // Buffering Logic
  private isBuffering: boolean = false;
  private minResumeBufferChunks = 2; // Wait for 2 chunks before resuming from starvation

  private aiMonitorId: number | null = null;
  private isStreamComplete = false;

  constructor(sampleRate: number = 24000, channelCount: number = 1) {
    this.sampleRate = sampleRate;
    this.channelCount = channelCount;
    console.log(`🎵 AudioPlayback initialized @ ${sampleRate}Hz`);
  }

  private initAudioContext() {
    if (!this.audioContext || this.audioContext.state === "closed") {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass({
        sampleRate: this.sampleRate,
      });
      console.log("🔊 AudioContext initialized");
    }

    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume();
    }
  }

  /**
   * Start a RAF loop that keeps the AI speaker indicator alive while playback runs.
   */
  private ensureAiMonitor() {
    if (typeof window === "undefined" || this.aiMonitorId !== null) {
      return;
    }

    const tick = () => {
      if (!this.isPlaying && !this.isBuffering) {
        this.stopAiMonitor();
        return;
      }

      const store = voiceAgentStore;

      // Sync buffering state
      if (store.isBuffering !== this.isBuffering) {
        store.isBuffering = this.isBuffering;
      }

      // Sync speaker state
      if (this.isPlaying || this.isBuffering) {
        if (store.speaker !== "ai") {
          store.speaker = "ai";
        }
      } else if (store.speaker === "ai") {
        store.speaker = "idle";
      }

      this.aiMonitorId = window.requestAnimationFrame(tick);
    };

    this.aiMonitorId = window.requestAnimationFrame(tick);
  }

  private stopAiMonitor() {
    if (typeof window === "undefined") {
      this.aiMonitorId = null;
      return;
    }

    if (this.aiMonitorId !== null) {
      cancelAnimationFrame(this.aiMonitorId);
      this.aiMonitorId = null;
    }

    // Reset store state
    const store = voiceAgentStore;
    if (store.isBuffering) store.isBuffering = false;
    if (store.speaker === "ai") store.speaker = "idle";
  }

  private convert16BitToFloat32(buffer: ArrayBuffer): Float32Array {
    const int16Data = new Int16Array(buffer);
    const float32Data = new Float32Array(int16Data.length);
    for (let i = 0; i < int16Data.length; i++) {
      float32Data[i] = int16Data[i] / 32768.0;
    }
    return float32Data;
  }

  /**
   * Add an audio chunk to the queue
   */
  addChunk(audioData: ArrayBuffer) {
    this.initAudioContext();
    if (!this.audioContext) return;

    const float32Data = this.convert16BitToFloat32(audioData);
    this.audioQueue.push(float32Data);

    if (this.isStreamComplete) {
      this.isStreamComplete = false;
    }

    // If we were buffering and have enough data, resume
    if (this.isBuffering) {
      if (
        this.audioQueue.length >= this.minResumeBufferChunks ||
        this.isStreamComplete
      ) {
        console.log(
          `▶️ Resuming playback from buffer (${this.audioQueue.length} chunks)`
        );
        this.isBuffering = false;
        this.isPlaying = true;
        this.scheduleQueue();
      }
    } else {
      // Normal playback
      if (!this.isPlaying) {
        console.log("🎬 Starting playback immediately");
        this.isPlaying = true;
        this.ensureAiMonitor();
        // Start slightly in the future to allow scheduling
        this.nextStartTime = this.audioContext.currentTime + 0.05;
      }
      this.scheduleQueue();
    }
  }

  private scheduleQueue() {
    if (!this.audioContext || this.isBuffering) return;

    const currentTime = this.audioContext.currentTime;

    // CRITICAL: Check for underrun (starvation)
    if (this.nextStartTime < currentTime) {
      if (this.audioQueue.length === 0 && !this.isStreamComplete) {
        // We ran out of data and stream isn't done -> Enter Buffering
        console.warn("⚠️ Buffer underrun! Entering buffering state.");
        this.isBuffering = true;
        this.isPlaying = false; // Pause playback logically
        // Reset nextStartTime so we don't try to play in the past when we resume
        this.nextStartTime = currentTime;
        return;
      } else {
        // We have data but fell behind -> Resync
        console.warn("⚠️ Timing slip detected. Resyncing clock.");
        this.nextStartTime = currentTime + 0.01;
      }
    }

    while (this.audioQueue.length > 0) {
      const chunk = this.audioQueue.shift();
      if (!chunk) break;

      const buffer = this.audioContext.createBuffer(
        this.channelCount,
        chunk.length,
        this.sampleRate
      );
      buffer.copyToChannel(chunk as any, 0);

      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);

      source.onended = () => {
        if (
          this.audioQueue.length === 0 &&
          !this.isBuffering &&
          !this.isStreamComplete
        ) {
          // Check if we really finished or just ran dry
          // This check happens when a source finishes playing.
          // If queue is empty, we might be starving soon.
          // The next scheduleQueue call will catch the actual starvation time.
        }

        if (
          this.audioQueue.length === 0 &&
          this.isStreamComplete &&
          Math.abs(this.nextStartTime - this.audioContext!.currentTime) < 0.1
        ) {
          this.handleStreamEnd();
        }
      };

      source.start(this.nextStartTime);
      this.nextStartTime += buffer.duration;
    }
  }

  markStreamComplete() {
    this.isStreamComplete = true;
    console.log("🏁 Stream marked complete");

    // If we were buffering, force flush whatever we have
    if (this.isBuffering && this.audioQueue.length > 0) {
      console.log("🔥 Flushing remaining buffer for stream end");
      this.isBuffering = false;
      this.isPlaying = true;
      // Reset time to now to play immediately
      if (this.audioContext) {
        this.nextStartTime = this.audioContext.currentTime + 0.05;
      }
      this.scheduleQueue();
    }
  }

  private handleStreamEnd() {
    console.log("✅ Playback finished");
    this.isPlaying = false;
    this.isBuffering = false;
    this.stopAiMonitor();
    this.resetStream();
  }

  resetStream() {
    this.isStreamComplete = false;
    this.audioQueue = [];
    this.isPlaying = false;
    this.isBuffering = false;
    this.nextStartTime = 0;
    this.stopAiMonitor();
  }

  stop() {
    console.log("⏹️ Stopping playback");
    if (this.audioContext) {
      this.audioContext.suspend();
    }
    this.resetStream();
  }

  clearQueue() {
    this.audioQueue = [];
    this.resetStream();
  }

  getSampleRate(): number {
    return this.sampleRate;
  }

  setSampleRate(sampleRate: number) {
    if (this.sampleRate === sampleRate) return;
    this.sampleRate = sampleRate;
    this.close(); // Re-init on next chunk
  }

  close() {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Singleton management
let audioPlaybackInstance: AudioPlayback | null = null;

export function getAudioPlayback(sampleRate: number = 24000): AudioPlayback {
  if (!audioPlaybackInstance) {
    audioPlaybackInstance = new AudioPlayback(sampleRate);
  } else if (audioPlaybackInstance.getSampleRate() !== sampleRate) {
    audioPlaybackInstance.setSampleRate(sampleRate);
  }
  return audioPlaybackInstance;
}

export function destroyAudioPlayback() {
  if (audioPlaybackInstance) {
    audioPlaybackInstance.close();
    audioPlaybackInstance = null;
  }
}
